/**
 * Constant editor dialog. column-mapping-ui spec, "Constant and value-map
 * editors": a fixed value for a target field with no source column (e.g.
 * Dostawca supplied as a filename rather than a column).
 */
import * as React from 'react';
import { ModalDialog } from '@ooh/ui/components/modal-dialog';
import { Button } from '@ooh/ui/components/button';
import { Input } from '@ooh/ui/components/input';
import {
  TransformDialogHeader,
  TRANSFORM_DIALOG_MAX_WIDTH,
} from './mapping-transforms-dialog-header';
import { EnumValueCombobox, type EnumValueComboboxLabels } from './enum-value-combobox';
import { EnumOptionsStatusBanner } from './enum-options-status-banner';

export interface ConstantEditorLabels {
  dialogTitle: string;
  columnLabelPrefix: string;
  valueFieldLabel: string;
  doneButtonLabel: string;
  /** CommandInput placeholder for the enum picker, e.g. "Szukaj wartości..." Optional so
   * apps/mockups' pre-existing labels object (typed against this interface but not updated
   * for the enum-picker addition) keeps compiling — omitting it falls back to an empty
   * placeholder, which the enum-picker branch never even renders in the mockup since it
   * never passes `enumOptions`. */
  valueSearchPlaceholder?: string;
  /** CommandEmpty text for the enum picker, e.g. "Brak wyników". Optional for the same
   * mockup-compat reason as valueSearchPlaceholder above. */
  valueNoResultsLabel?: string;
  /** Shown instead of the combobox/input while a query-backed enum field's options (e.g.
   * media formats) are still loading. Optional for the same mockup-compat reason as
   * valueSearchPlaceholder above — the static-enum fields (type/material/lighting) never
   * pass `enumOptionsLoading`, so this label is never read for them. */
  enumOptionsLoadingLabel?: string;
  /** Shown instead of the combobox/input when a query-backed enum field's options failed to
   * load. Optional for the same reason as enumOptionsLoadingLabel above. */
  enumOptionsErrorLabel?: string;
}

export interface ConstantEditorAllowedValue {
  value: string;
  label: string;
}

export interface ConstantEditorDialogProps {
  sourceColumnLabel: string;
  initialValue: string;
  onChange?: (value: string) => void;
  labels: ConstantEditorLabels;
  /** When provided and non-empty, renders a Select of these values instead of a free-text
   * Input — for target fields with a closed set of values (e.g. "Typ nośnika" / key `type`),
   * per column-mapping-ui spec: a closed-enum field must not accept an arbitrary typo-prone
   * string as its constant value. */
  allowedValues?: ConstantEditorAllowedValue[];
  onClose: () => void;
  /** The target field's closed value set (e.g. media type, material, lighting) — when
   * provided, renders a searchable dropdown instead of free text so the operator can't
   * mistype an enum-backed constant (CU-86can7jwy: "Fix XLS import: required column
   * mapping dropdown for enum fields"). Omit for open-ended fields (price, name, notes). */
  enumOptions?: readonly string[];
  /** True while a query-backed enum field's options (e.g. media formats) are being fetched —
   * renders a loading state instead of an empty combobox or a misleading free-text fallback.
   * The 3 static-enum fields (type/material/lighting) never pass this. */
  enumOptionsLoading?: boolean;
  /** True when a query-backed enum field's options failed to load — renders an inline error
   * instead of silently falling back to free text. */
  enumOptionsError?: boolean;
}

/** Builds the labels object for the enum-picker combobox — pulled out of ConstantValueField
 * to keep that component's cyclomatic complexity under the lint limit (each `??` fallback
 * counts as a branch). */
function buildEnumPickerLabels(labels: ConstantEditorLabels): EnumValueComboboxLabels {
  return {
    searchPlaceholder: labels.valueSearchPlaceholder ?? '',
    noResultsLabel: labels.valueNoResultsLabel ?? '',
    placeholderLabel: labels.valueFieldLabel,
  };
}

interface ConstantValueFieldProps {
  value: string;
  labels: ConstantEditorLabels;
  allowedValues?: ConstantEditorAllowedValue[];
  enumOptions?: readonly string[];
  enumOptionsLoading?: boolean;
  enumOptionsError?: boolean;
  onChange: (value: string) => void;
}

/** Builds props for the allowedValues EnumValueCombobox — maps translated labels to enum
 * values so the UI shows readable names while the stored constant stays the canonical enum
 * string (e.g. "Billboard" shown, "billboard" stored). Extracted to reduce ConstantValueField
 * complexity. */
function buildAllowedValuesComboboxProps(
  allowedValues: ConstantEditorAllowedValue[],
  value: string,
  onChange: (value: string) => void,
): { value: string; options: string[]; onChange: (label: string) => void } {
  const labelToValue = Object.fromEntries(allowedValues.map((o) => [o.label, o.value]));
  const selectedLabel = allowedValues.find((o) => o.value === value)?.label ?? value;
  return {
    value: selectedLabel,
    options: allowedValues.map((o) => o.label),
    onChange: (label: string) => onChange(labelToValue[label] ?? label),
  };
}

/** The constant-value field's editor widget — extracted out of ConstantEditorDialog to
 * keep that component's complexity low; all the allowed-values/enum/loading/error/free-text
 * branching lives here instead (mirrors ValueMapTargetCell in
 * mapping-transforms-value-map-dialog.tsx). */
function ConstantValueField({
  value,
  labels,
  allowedValues,
  enumOptions,
  enumOptionsLoading,
  enumOptionsError,
  onChange,
}: ConstantValueFieldProps) {
  if (enumOptionsError) {
    return (
      <EnumOptionsStatusBanner
        variant="error"
        message={labels.enumOptionsErrorLabel ?? ''}
        testId="constant-value-enum-error"
      />
    );
  }
  if (enumOptionsLoading) {
    return (
      <EnumOptionsStatusBanner
        variant="loading"
        message={labels.enumOptionsLoadingLabel ?? ''}
        testId="constant-value-enum-loading"
      />
    );
  }
  if (allowedValues && allowedValues.length > 0) {
    const comboboxProps = buildAllowedValuesComboboxProps(allowedValues, value, onChange);
    return (
      <EnumValueCombobox
        {...comboboxProps}
        labels={buildEnumPickerLabels(labels)}
        testId="constant-value-enum-picker"
      />
    );
  }
  if (enumOptions && enumOptions.length > 0) {
    return (
      <EnumValueCombobox
        value={value}
        options={enumOptions}
        onChange={onChange}
        labels={buildEnumPickerLabels(labels)}
        testId="constant-value-enum-picker"
      />
    );
  }
  return (
    <Input
      id="constant-value-input"
      value={value}
      onChange={(e) => onChange(e.target.value)}
      data-testid="constant-value-input"
    />
  );
}

export function ConstantEditorDialog({
  sourceColumnLabel,
  initialValue,
  onChange,
  labels,
  allowedValues,
  onClose,
  enumOptions,
  enumOptionsLoading,
  enumOptionsError,
}: ConstantEditorDialogProps) {
  const [value, setValue] = React.useState(initialValue);

  const handleChange = (next: string) => {
    setValue(next);
    onChange?.(next);
  };

  return (
    <ModalDialog
      header={
        <TransformDialogHeader
          title={labels.dialogTitle}
          columnLabelPrefix={labels.columnLabelPrefix}
          sourceColumnLabel={sourceColumnLabel}
        />
      }
      onClose={onClose}
      maxWidth={TRANSFORM_DIALOG_MAX_WIDTH}
      testId="constant-editor-dialog"
      footer={
        <Button type="button" onClick={onClose} data-testid="constant-editor-done">
          {labels.doneButtonLabel}
        </Button>
      }
    >
      <div className="space-y-2" data-testid="constant-editor">
        <label
          htmlFor="constant-value-input"
          className="text-xs font-medium text-muted-foreground block"
        >
          {labels.valueFieldLabel}
        </label>
        <ConstantValueField
          value={value}
          labels={labels}
          allowedValues={allowedValues}
          enumOptions={enumOptions}
          enumOptionsLoading={enumOptionsLoading}
          enumOptionsError={enumOptionsError}
          onChange={handleChange}
        />
      </div>
    </ModalDialog>
  );
}
